import { useMemo, useState, type ReactNode } from 'react';
import type { ConversationTranscript } from '@/lib/api';

interface CallContextBarProps {
  summary: string;
  /** Newest-first list of all calls. When omitted/empty, falls back to
   *  the single legacy `transcript` prop so older Worker responses still
   *  render. */
  transcripts?: ConversationTranscript[];
  transcript: ConversationTranscript | null;
  /**
   * 2026-07-02 (ReminderPillInline) — extra chip(s) rendered INSIDE the same
   * scrollable chip row, after Summary/Transcript (e.g. the ⏰ reminder pill).
   * When present, the row renders even with no summary/transcript so the
   * "Remind me" affordance always has a home.
   */
  trailing?: ReactNode;
}

interface ParsedTurn {
  speaker: string;
  isShelby: boolean;
  text: string;
}

function parseTranscript(raw: string): ParsedTurn[] {
  const turns: ParsedTurn[] = [];
  for (const line of raw.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const m = /^([A-Za-z][A-Za-z .'-]{0,30}):\s*(.*)$/.exec(trimmed);
    if (m && m[1] && m[2] !== undefined) {
      const speaker = m[1].trim();
      turns.push({
        speaker,
        isShelby: /shelby|agent|assistant/i.test(speaker),
        text: m[2].trim(),
      });
    } else if (turns.length > 0) {
      const last = turns[turns.length - 1];
      if (last) last.text = `${last.text} ${trimmed}`.trim();
    } else {
      turns.push({ speaker: 'Note', isShelby: false, text: trimmed });
    }
  }
  return turns;
}

function formatDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const summaryIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const headphonesIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
    <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
    <path d="M21 19a2 2 0 0 1-2 2h-1v-7h3v5zM3 19a2 2 0 0 0 2 2h1v-7H3v5z" />
  </svg>
);

const closeIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/**
 * Compact "call context" bar — two pinned chips above the SMS thread:
 *   [📝 Summary]   [🎧 Transcript · 5:07]
 *
 * Tap either chip to open the full content as a bottom-sheet modal over the
 * SMS thread. This replaces the older pattern where the summary banner +
 * transcript card were rendered as full blocks at the top of the scroll
 * region (which buried them once you scrolled, or ate 60%+ of the screen
 * when pinned). Ernie 2026-05-12 PM: "we need a different solution — make
 * it stand out and not disappear, but don't eat the whole screen."
 *
 * Self-contained: owns sheet open state, renders backdrop + sheet inline.
 */
function formatCallLabel(t: ConversationTranscript): string {
  // Friendly date — "May 12, 9:00 PM" — for the call-list rows. Falls
  // back to just the duration when iso isn't parseable.
  const iso = t.iso ?? '';
  if (iso) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }
  }
  return 'Earlier call';
}

export function CallContextBar({ summary, transcripts, transcript, trailing }: CallContextBarProps) {
  const [open, setOpen] = useState<null | 'summary' | 'transcript'>(null);
  // When viewing a multi-call thread, this tracks which call's transcript
  // is expanded inside the bottom sheet. Defaults to 0 (newest) on open;
  // null = show the list view instead of an expanded transcript.
  const [activeCallIndex, setActiveCallIndex] = useState<number>(0);

  const trimmedSummary = (summary ?? '').trim();
  const hasSummary = trimmedSummary.length > 0;

  // Prefer the new transcripts array; fall back to the legacy single-
  // transcript prop so the chip still works against older Worker builds.
  const calls = useMemo<ConversationTranscript[]>(() => {
    if (transcripts && transcripts.length > 0) return transcripts;
    if (transcript) return [transcript];
    return [];
  }, [transcripts, transcript]);

  const hasTranscripts = calls.some((c) => (c.text ?? '').trim().length > 0);
  const callCount = calls.length;
  const activeCall = calls[activeCallIndex] ?? calls[0];

  const turns = useMemo(
    () => (activeCall ? parseTranscript(activeCall.text) : []),
    [activeCall],
  );

  const latestDuration =
    callCount === 1 &&
    calls[0] &&
    Number.isFinite(calls[0].duration_sec) &&
    calls[0].duration_sec > 0
      ? formatDuration(calls[0].duration_sec)
      : '';

  if (!hasSummary && !hasTranscripts && !trailing) return null;

  return (
    <>
      {/* Chip row — sits pinned just under ActionGrid. ~48px tall so it
          never crowds the SMS thread underneath. */}
      <div className="flex items-center gap-2 overflow-x-auto px-4 pb-3 pt-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {hasSummary && (
          <button
            type="button"
            onClick={() => setOpen('summary')}
            aria-label="View Shelby's call summary"
            className="ss-press flex flex-shrink-0 items-center gap-1.5 rounded-full border border-ss-blue/30 bg-ss-blue-light px-3 py-1.5 text-xs font-semibold text-ss-blue-dark active:bg-ss-blue/15"
          >
            <span className="text-ss-blue">{summaryIcon}</span>
            <span>Summary</span>
          </button>
        )}
        {hasTranscripts && (
          <button
            type="button"
            onClick={() => {
              setActiveCallIndex(0);
              setOpen('transcript');
            }}
            aria-label={
              callCount > 1
                ? `View ${callCount} calls`
                : 'View call transcript'
            }
            className="ss-press flex flex-shrink-0 items-center gap-1.5 rounded-full border border-ss-blue/30 bg-ss-blue-light px-3 py-1.5 text-xs font-semibold text-ss-blue-dark active:bg-ss-blue/15"
          >
            <span className="text-ss-blue">{headphonesIcon}</span>
            <span>
              {callCount > 1
                ? `Calls · ${callCount}`
                : latestDuration
                  ? `Transcript · ${latestDuration}`
                  : 'Transcript'}
            </span>
          </button>
        )}
        {trailing}
      </div>

      {/* Bottom sheet — slides up over the SMS thread on chip tap. Mirrors
          QuickReplySheet pattern for consistency. */}
      {open && (
        <div
          // z-[60] sits ABOVE the ReplyBar (position:fixed, zIndex:50) so the
          // sheet covers it instead of being occluded by it. At z-40 the reply
          // bar painted over the bottom of the call list — the 2nd "Calls · N"
          // row was clipped behind it and couldn't be scrolled into view on
          // iOS standalone (Ernie 2026-06-05, Item 2). The sheet's own
          // safe-area bottom padding clears the home indicator; the internal
          // scroll (maxHeight + overflow-y-auto below) is the documented
          // 2026-06-01 iOS+desktop fix and is left untouched.
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40"
          onClick={() => setOpen(null)}
          role="dialog"
          aria-modal="true"
          aria-label={open === 'summary' ? "Shelby's summary" : 'Call transcript'}
        >
          <div
            className="flex w-full max-w-md flex-col rounded-t-2xl bg-white dark:bg-[#0F172A] shadow-ss-md animate-ss-slide-up"
            onClick={(e) => e.stopPropagation()}
            style={{
              // Cap the sheet at 85vh so the top can't escape the viewport
              // on tall desktop windows. Without this cap, a long transcript
              // grew the sheet past 100vh; items-end on the overlay anchored
              // the bottom to the viewport floor and pushed the header +
              // start-of-transcript off-screen upward — symptom: "I can't
              // see the whole transcript, I can't scroll down" (Ernie
              // 2026-06-01, desktop browser).
              //
              // The scroll-child below STILL carries its own max-height so
              // iOS Safari's scroll engagement keeps working. With both
              // caps in place, the sheet is bounded by 85vh on every
              // browser AND the scroll-child has a definite max-height for
              // iOS Safari's flex-1-doesn't-honor-parent-maxHeight quirk.
              maxHeight: '85vh',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
            }}
          >
            <div className="flex justify-center pt-2">
              <span className="h-1.5 w-12 rounded-full bg-ink-200 dark:bg-[#334155]" />
            </div>
            <div className="flex items-center justify-between px-5 pb-3 pt-3">
              <div className="flex min-w-0 items-center gap-2">
                {open === 'transcript' && callCount > 1 && activeCallIndex >= 0 && (
                  <button
                    type="button"
                    onClick={() => setActiveCallIndex(-1)}
                    aria-label="Back to call list"
                    className="ss-press flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-ss-blue active:bg-ss-blue/10"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                  </button>
                )}
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-ss-blue-light text-ss-blue">
                  {open === 'summary' ? summaryIcon : headphonesIcon}
                </span>
                <div className="min-w-0 truncate text-[15px] font-semibold text-ink-900 dark:text-[#E2E8F0]">
                  {open === 'summary'
                    ? "Shelby's summary"
                    : callCount > 1 && activeCallIndex < 0
                      ? `Calls · ${callCount}`
                      : activeCall
                        ? `Call · ${formatCallLabel(activeCall)}${
                            activeCall.duration_sec > 0
                              ? ` · ${formatDuration(activeCall.duration_sec)}`
                              : ''
                          }`
                        : 'Call transcript'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(null)}
                aria-label="Close"
                className="ss-press ml-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-ink-100 dark:bg-[#1E293B] text-ink-700 dark:text-[#94A3B8]"
              >
                {closeIcon}
              </button>
            </div>
            <div
              className="overflow-y-auto overscroll-contain px-5 pb-4 text-[14px] leading-relaxed text-ink-800 dark:text-[#E2E8F0]"
              style={{
                // Definite max-height + overflow-y-auto on a plain block.
                //
                // History:
                // - Originally was maxHeight: '80vh' on the parent SHEET. That
                //   broke iOS Safari scrolling (Apple's flex-child sizing).
                // - Moved to "min-h-0 flex-1 + child maxHeight". Fixed iOS but
                //   broke desktop Chrome — the flex-1 vs. maxHeight resolution
                //   on a parent without a definite height meant Chrome computed
                //   the child at its content height, never engaging overflow.
                //   Symptom: long transcripts visibly clipped on desktop with
                //   no scrollbar.
                // - Current: drop flex-1 + min-h-0. Plain block with maxHeight +
                //   overflow-y-auto + overscroll-contain. Doesn't depend on
                //   flex sizing rules — every browser respects it the same way.
                //   iOS Safari fix preserved (child carries its own definite
                //   constraint, parent stays content-determined).
                maxHeight: 'calc(80vh - 90px)',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {open === 'summary' ? (
                <p className="whitespace-pre-wrap">{trimmedSummary}</p>
              ) : callCount > 1 && activeCallIndex < 0 ? (
                /* Call list — one row per past call. Tap a row to expand
                   that call's transcript in this same sheet. */
                <ul className="flex flex-col gap-1.5">
                  {calls.map((c, i) => (
                    <li key={c.iso || `call-${i}`}>
                      <button
                        type="button"
                        onClick={() => setActiveCallIndex(i)}
                        className="ss-press flex w-full items-center justify-between rounded-xl border border-ink-100 dark:border-[#334155] bg-ink-50 dark:bg-[#1E293B] px-3 py-2.5 text-left"
                      >
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold text-ink-900 dark:text-[#E2E8F0]">
                            {formatCallLabel(c)}
                            {i === 0 && (
                              <span className="ml-2 rounded-full bg-ss-blue/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-ss-blue">
                                Latest
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 truncate text-xs text-ink-500 dark:text-[#94A3B8]">
                            {c.duration_sec > 0
                              ? `${formatDuration(c.duration_sec)} · ${
                                  (c.text ?? '').length > 0
                                    ? 'Tap to view transcript'
                                    : 'Recording only'
                                }`
                              : 'Tap to view'}
                          </div>
                        </div>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="ml-2 h-4 w-4 flex-shrink-0 text-ink-400">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : turns.length === 0 ? (
                <p className="text-ink-500 dark:text-[#94A3B8]">No transcript available.</p>
              ) : (
                <div>
                  {turns.map((t, i) => (
                    <div key={i} className="mb-2.5 last:mb-0">
                      <span
                        className={`mr-1 font-bold ${t.isShelby ? 'text-ss-blue' : 'text-ink-900 dark:text-[#E2E8F0]'}`}
                      >
                        {t.speaker}:
                      </span>
                      <span>{t.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
